'use client'

import { createContext, useEffect, useMemo } from 'react'
import { useSession } from 'session/src/hooks/useSession'
import useSWRMutation from 'swr/mutation'
import type { User } from 'types/session/user'
import {
	userDetailsService,
	userDetailsUrl,
} from 'user-data/services/userDetailsService'

interface UserDetailsContext {
	userDetails: User | null | undefined
	revalidateUserDetails: () => void
}

const defaultContext = {
	revalidateUserDetails: async () => {},
	userDetails: null,
}

const UserDetailsContext = createContext<UserDetailsContext>(defaultContext)

interface UserDetailsProviderProps {
	children: React.ReactNode
}

const UserDetailsProvider = ({
	children,
}: Readonly<UserDetailsProviderProps>): React.ReactNode => {
	const { logged, token, externalId } = useSession()

	const { trigger, data } = useSWRMutation(
		logged && token ? userDetailsUrl : null,
		() => userDetailsService(token, externalId)
	)

	useEffect(() => {
		if (logged && token) {
			trigger()
		}
	}, [logged, token])

	const value = useMemo(
		() => ({
			userDetails: data,
			revalidateUserDetails: trigger,
		}),
		[data, trigger]
	)

	return (
		<UserDetailsContext.Provider value={value}>
			{children}
		</UserDetailsContext.Provider>
	)
}

export { UserDetailsProvider, UserDetailsContext }
